import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DatePicker from "react-datepicker"
import { Dropdown } from 'react-bootstrap'
import { startOfMonth, endOfMonth, format } from 'date-fns'
import Select from 'react-select'
import XLSX from 'xlsx'

import useDidMountEffect from 'pages/parts/useDidMountEffect'
import SummaryLoyalty from 'pages/main/reports/SummaryLoyalty'
import SummaryExpense from 'pages/main/reports/SummaryExpense'
import SummaryExpenseIncome from 'pages/main/reports/SummaryExpenseIncome'
import SummaryIncomes from 'pages/main/reports/SummaryIncomes'
import SummaryDebt from 'pages/main/reports/SummaryDebt'
import SummaryCash from 'pages/main/reports/SummaryCash'
import SummaryBank from 'pages/main/reports/SummaryBank'
import SummarySalary from 'pages/main/reports/SummarySalary'
import SummarySales from 'pages/main/reports/SummarySales'
import SummaryAllowances from 'pages/main/reports/SummaryAllowances'
import SummaryGrossIncome from 'pages/main/reports/SummaryGrossIncome'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, returnSign, formatDate } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"
import { businessMonthStart } from 'helpers/businessDay'

// Единое описание колонок: по нему строится и таблица, и выгрузка в Excel — чтобы файл был 1в1 с отчётом
// label — ключ перевода ('%' выводится как есть), field — поле дня, overall — поле итоговой строки,
// modal — какая модалка открывается по клику, decimals — знаков после запятой
const columns = [
	{ label: 'sales', field: 'sales', overall: 'overall_sales' },
	{ label: 'allowances', field: 'allowances', overall: 'overall_allowances', modal: 'allowances' },
	{ label: 'sales_amount', field: 'sales_total_amount', overall: 'sales_overall', modal: 'sales' },
	{ label: 'gross_income', field: 'gross_income_total_amount', overall: 'gross_incomes_overall', modal: 'gross_income' },
	{ label: '%', field: 'gross_income_percentage', overall: 'overall_gross_income_percentage', decimals: 2, modal: 'gross_income' },
	{ label: 'good_reception', field: 'documents_in_total_amount', overall: 'documents_in_overall', modal: 'incomes' },
	{ label: '%', field: 'documents_in_total_percentage', overall: 'overall_documents_in_percentage', decimals: 2 },
	{ label: 'paid_supplier', field: 'supplier_total_amount', overall: 'supplier_overall' },
	{ label: '%', field: 'suppliers_percentage', overall: 'overall_suppliers_percentage', decimals: 2 },
	{ label: 'discount', field: 'total_discount', overall: 'cheque_discount_overall' },
	{ label: '%', field: 'total_discount_percentage', overall: 'overall_discount_percentage', decimals: 2 },
	{ label: 'expense', field: 'expenses_total_amount', overall: 'expenses_overall', modal: 'expenses' },
	{ label: '%', field: 'expenses_percentage', overall: 'overall_expenses_percentage', decimals: 2, modal: 'expenses' },
	{ label: 'income', field: 'incomes_total_amount', overall: 'incomes_overall', modal: 'expenses_incomes' },
	{ label: '%', field: 'incomes_percentage', overall: 'overall_income_percentage', decimals: 2, modal: 'expenses_incomes' },
	{ label: 'salary', field: 'salary_debt', overall: 'salary_debt_overall', modal: 'salary' },
	{ label: '%', field: 'salary_debt_percentage', overall: 'overall_salary_percentage', decimals: 2, modal: 'salary' },
	{ label: 'paid', field: 'salary_total_paid', overall: 'salary_overall', modal: 'salary' },
	{ label: 'salary_balance', field: 'salary_balance', overall: 'salary_balance_overall', modal: 'salary' },
	{ label: 'cash', field: 'cash_total_amount', overall: 'cash_overall', modal: 'cash' },
	{ label: 'bank', field: 'bank_total_amount', overall: 'bank_overall', modal: 'bank' },
	{ label: 'loyalty', field: 'loyalty_amount_out', overall: 'loyalty_overall', modal: 'loyalty' },
	{ label: 'debt', field: 'debt_total_amount_out', overall: 'overall_debt', modal: 'debt' },
	{ label: '%', field: 'debtors_percentage', overall: 'overall_debt_percentage', decimals: 2, modal: 'debt' },
	{ label: 'operational_result', field: 'net_profit', overall: 'net_profit_overall', tooltip: true },
	{ label: '%', field: 'net_profit_percentage', overall: 'overall_net_profit_percentage', decimals: 2 },
]

// Число для Excel: пустое значение или NaN превращаем в 0, проценты округляем
function excelNumber(value, decimals = 0) {
	var number = Number(value)
	if (!isFinite(number)) return 0
	return Number(number.toFixed(decimals))
}

function Summary() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState([])
	const [menu, setMenu] = useState([])
	const [poses, setPoses] = useState([])
	const [overall, setOverall] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: businessMonthStart(),
		pos_id: reduxAccount.pos_id,
		type: null,
		random: 0,
		random_active_modal: 0,
	})
	const [activeModal, setActiveModal] = useState(0)

	async function searchWithFilter() {
		var filterDataCopy = { ...filterData }
		var filterUrl = `/report-summary`
		var urlParams = ""

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.menu_ids && filterDataCopy.menu_ids.length > 0) {
			const ids = filterDataCopy.menu_ids.map(item => item.id).join(',');
			urlParams += returnSign(urlParams) + 'menu_ids=' + ids;
		}
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setOverall(response.data)
			setData(response.data.data)
		}

		setDropdownIsOpen(false)
	}

	async function exportToExcel() {
		var excelHeaders = [[
			t('date'),
			...columns.map(column => column.label === '%' ? '%' : t(column.label))
		]]

		for (let i = 0; i < data.length; i++) {
			var excelItems = [formatDate(data[i].days)]
			for (let j = 0; j < columns.length; j++) {
				excelItems.push(excelNumber(data[i][columns[j].field], columns[j].decimals))
			}
			excelHeaders.push(excelItems)
		}

		if (data.length > 0) {
			excelHeaders.push([
				t('total'),
				...columns.map(column => excelNumber(overall[column.overall], column.decimals))
			])
		}

		const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('summary_report') + ".xlsx");
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function handleSelectedDate(type, month = true, date) {
		if (month) {
			setFilterData({
				...filterData,
				'start_date': format(startOfMonth(new Date(filterData.start_date)), 'yyyy-MM-dd'),
				'end_date': format(endOfMonth(new Date(filterData.start_date)), 'yyyy-MM-dd'),
				'type': type,
				'random_active_modal': filterData.random_active_modal + 1,
			})
		} else {
			setFilterData({
				...filterData,
				'start_date': date,
				'end_date': date,
				'type': type,
				'random_active_modal': filterData.random_active_modal + 1,
			})
		}
	}

	async function getPoses() {
		var response = await GET('/helper/poses')
		if (httpOk(response)) {
			setPoses(response.data)
			if (response.data.length > 0) {
				setFilterData((prev) => ({
					...prev,
					'pos_id': prev.pos_id || response.data[0]['id'],
					'random': prev.random + 1,
				}))
			}
		}
	}

	async function getMenu() {
		const response = await GET('/helper/menu', {}, { loader: true })
		if (httpOk(response)) setMenu(response.data)
	}

	useEffect(() => {
		getPoses()
		getMenu()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		searchWithFilter()
	}, [filterData.random]) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		setActiveModal(filterData.type)
	}, [filterData.random_active_modal]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('summary_report')}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('date')}</label>
												<div className="calendar-input">
													<DatePicker
														showMonthDropdown
														showYearDropdown
														dropdownMode="select"
														yearDropdownItemNumber={2}
														minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
														maxDate={new Date(new Date().getFullYear(), 11, 31)}
														showMonthYearPicker
														showFullMonthYearPicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('menu')}</label>
												<Select
													isMulti
													isClearable
													options={menu}
													value={filterData.menu_ids}
													onChange={(options) => setFilterData({ ...filterData, 'menu_ids': options ? options : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter(false)}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2"
										onClick={() => exportToExcel()}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
					</div>

					<div className="table-responsive table-fixed mb-3">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th>{t('date')}</th>
									{columns.map((column, index) => (
										<th key={index}
											style={{ 'minWidth': column.label === '%' ? '30px' : '70px' }}
											className="text-end td-ellipsis"
											title={column.label === '%' ? '%' : t(column.label)}>
											{column.tooltip ?
												<CustomTooltip text={`${t(column.label)} = ${t('gross_income')} - ${t('discount')} - ${t('expense')} - ${t('salary')}`}>
													{t(column.label)}
												</CustomTooltip>
												:
												<>{column.label === '%' ? '%' : t(column.label)}</>
											}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => (
									<tr key={index}>
										<td>{formatDate(item.days)}</td>
										{columns.map((column, index2) => (
											<td key={index2}
												className={`text-end text-nowrap ${column.modal ? 'hover' : ''}`}
												onClick={() => column.modal && handleSelectedDate(column.modal, false, item.days)}>
												{formatMoney(item[column.field], column.decimals)}
											</td>
										))}
									</tr>
								))}
							</tbody>
							<tfoot>
								{data.length > 0 &&
									<tr>
										<th><b>{t('total')}</b></th>
										{columns.map((column, index) => (
											<th key={index}
												className={`text-end text-nowrap ${column.modal ? 'hover' : ''}`}
												onClick={() => column.modal && handleSelectedDate(column.modal, true)}>
												<b>{formatMoney(overall[column.overall], column.decimals)}</b>
											</th>
										))}
									</tr>
								}
							</tfoot>
						</table>
					</div>

					<div className="w-100 d-flex justify-content-between">
						<div className="flex-column">
							<h5 className="d-flex justify-content-between gap-3">
								<strong>{t('cheques')}:</strong>
								<span>{formatMoney(overall.total_cheques)}</span>
							</h5>
							<h5 className="d-flex justify-content-between gap-3">
								<strong>{t('guests')}:</strong>
								<span>{formatMoney(overall.total_people_number)}</span>
							</h5>
						</div>
					</div>
				</div>
			</div>

			{activeModal === 'expenses' &&
				<SummaryExpense
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryExpense>
			}
			{activeModal === 'expenses_incomes' &&
				<SummaryExpenseIncome
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryExpenseIncome>
			}
			{activeModal === 'incomes' &&
				<SummaryIncomes
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryIncomes>
			}
			{activeModal === 'cash' &&
				<SummaryCash
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryCash>
			}
			{activeModal === 'bank' &&
				<SummaryBank
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryBank>
			}
			{activeModal === 'debt' &&
				<SummaryDebt
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryDebt>
			}
			{activeModal === 'salary' &&
				<SummarySalary
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummarySalary>
			}
			{activeModal === 'loyalty' &&
				<SummaryLoyalty
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryLoyalty>
			}
			{activeModal === 'sales' &&
				<SummarySales
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummarySales>
			}
			{activeModal === 'allowances' &&
				<SummaryAllowances
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryAllowances>
			}
			{activeModal === 'gross_income' &&
				<SummaryGrossIncome
					setActiveModal={setActiveModal}
					filterData={filterData}>
				</SummaryGrossIncome>
			}
		</>
	)
}

export default Summary